import React, { useState } from 'react';
import './DeviceAddModal.css';

const DeviceAddModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    deviceId: '',
    name: '',
    location: {
      address: '',
      latitude: '',
      longitude: '',
      district: ''
    },
    specifications: {
      model: '',
      manufacturer: '',
      screenSize: '',
      resolution: '',
      brightness: '',
      powerConsumption: '',
      installationDate: ''
    },
    region: 'ulsan',
    network: {
      ipAddress: '',
      port: '',
      connectionType: 'tcp'
    }
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // 폼 데이터 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 폼 검증
  const validateForm = () => {
    const newErrors = {};

    if (!formData.deviceId.trim()) {
      newErrors.deviceId = '장비 ID는 필수입니다.';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.deviceId)) {
      newErrors.deviceId = '장비 ID는 영문 대문자, 숫자, _, -만 사용 가능합니다.';
    }

    if (!formData.name.trim()) {
      newErrors.name = '장비 이름은 필수입니다.';
    }

    if (!formData.location.address.trim()) {
      newErrors.address = '주소는 필수입니다.';
    }

    if (!formData.region) {
      newErrors.region = '지역을 선택해주세요.';
    }

    if (formData.network.ipAddress &&
      !/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(formData.network.ipAddress)) {
      newErrors.ipAddress = '올바른 IP 주소 형식을 입력해주세요.';
    }

    if (formData.network.port && (isNaN(formData.network.port) || formData.network.port < 1 || formData.network.port > 65535)) {
      newErrors.port = '포트는 1-65535 사이의 숫자여야 합니다.';
    }

    return newErrors;
  };

  // 테스트 장비 생성
  const generateTestDevice = () => {
    const testId = `TEST_${Date.now()}`;
    const testData = {
      deviceId: testId,
      name: `테스트 장비 ${testId.slice(-6)}`,
      location: {
        address: '울산광역시 남구 삼산로 100',
        latitude: '35.5384',
        longitude: '129.3114',
        district: '남구'
      },
      specifications: {
        model: 'ULS-LED-2024',
        manufacturer: 'ULSAN TECH',
        screenSize: '3x2m',
        resolution: '1920x1080',
        brightness: '8000',
        powerConsumption: '500W',
        installationDate: new Date().toISOString().split('T')[0]
      },
      region: 'ulsan',
      network: {
        ipAddress: `192.168.1.${Math.floor(Math.random() * 200) + 50}`,
        port: '8080',
        connectionType: 'tcp'
      }
    };

    setFormData(testData);
  };

  // 폼 제출
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await onAdd(formData);
      onClose();
      // 폼 초기화
      setFormData({
        deviceId: '',
        name: '',
        location: {
          address: '',
          latitude: '',
          longitude: '',
          district: ''
        },
        specifications: {
          model: '',
          manufacturer: '',
          screenSize: '',
          resolution: '',
          brightness: '',
          powerConsumption: '',
          installationDate: ''
        },
        region: 'ulsan',
        network: {
          ipAddress: '',
          port: '',
          connectionType: 'tcp'
        }
      });
    } catch (error) {
      console.error('장비 추가 오류:', error);
      setErrors({ submit: error.message || '장비 추가에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="device-add-modal-overlay">
      <div className="device-add-modal">
        <div className="modal-header">
          <h2>🖥️ 새 장비 추가</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="device-add-form">
          {/* 기본 정보 */}
          <div className="form-section">
            <h3>📋 기본 정보</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deviceId">장비 ID *</label>
                <input
                  type="text"
                  id="deviceId"
                  name="deviceId"
                  value={formData.deviceId}
                  onChange={handleInputChange}
                  placeholder="예: ULS_LED_001"
                  className={errors.deviceId ? 'error' : ''}
                  required
                />
                {errors.deviceId && <span className="error-message">{errors.deviceId}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="name">장비 이름 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="예: 남구청 앞 전광판"
                  className={errors.name ? 'error' : ''}
                  required
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="region">지역 *</label>
                <select
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  className={errors.region ? 'error' : ''}
                  required
                >
                  <option value="ulsan">울산광역시</option>
                  <option value="jung">중구</option>
                  <option value="nam">남구</option>
                  <option value="dong">동구</option>
                  <option value="buk">북구</option>
                  <option value="ulju">울주군</option>
                </select>
                {errors.region && <span className="error-message">{errors.region}</span>}
              </div>
            </div>
          </div>

          {/* 위치 정보 */}
          <div className="form-section">
            <h3>📍 위치 정보</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="location.address">주소 *</label>
                <input
                  type="text"
                  id="location.address"
                  name="location.address"
                  value={formData.location.address}
                  onChange={handleInputChange}
                  placeholder="예: 울산광역시 남구 삼산로 100"
                  className={errors.address ? 'error' : ''}
                  required
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location.latitude">위도</label>
                <input
                  type="text"
                  id="location.latitude"
                  name="location.latitude"
                  value={formData.location.latitude}
                  onChange={handleInputChange}
                  placeholder="35.5384"
                />
              </div>

              <div className="form-group">
                <label htmlFor="location.longitude">경도</label>
                <input
                  type="text"
                  id="location.longitude"
                  name="location.longitude"
                  value={formData.location.longitude}
                  onChange={handleInputChange}
                  placeholder="129.3114"
                />
              </div>

              <div className="form-group">
                <label htmlFor="location.district">구역</label>
                <input
                  type="text"
                  id="location.district"
                  name="location.district"
                  value={formData.location.district}
                  onChange={handleInputChange}
                  placeholder="남구"
                />
              </div>
            </div>
          </div>

          {/* 장비 사양 */}
          <div className="form-section">
            <h3>⚙️ 장비 사양</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="specifications.model">모델명</label>
                <input
                  type="text"
                  id="specifications.model"
                  name="specifications.model"
                  value={formData.specifications.model}
                  onChange={handleInputChange}
                  placeholder="ULS-LED-2024"
                />
              </div>

              <div className="form-group">
                <label htmlFor="specifications.manufacturer">제조사</label>
                <input
                  type="text"
                  id="specifications.manufacturer"
                  name="specifications.manufacturer"
                  value={formData.specifications.manufacturer}
                  onChange={handleInputChange}
                  placeholder="ULSAN TECH"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="specifications.screenSize">화면 크기</label>
                <input
                  type="text"
                  id="specifications.screenSize"
                  name="specifications.screenSize"
                  value={formData.specifications.screenSize}
                  onChange={handleInputChange}
                  placeholder="3x2m"
                />
              </div>

              <div className="form-group">
                <label htmlFor="specifications.resolution">해상도</label>
                <input
                  type="text"
                  id="specifications.resolution"
                  name="specifications.resolution"
                  value={formData.specifications.resolution}
                  onChange={handleInputChange}
                  placeholder="1920x1080"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="specifications.brightness">밝기 (nit)</label>
                <input
                  type="text"
                  id="specifications.brightness"
                  name="specifications.brightness"
                  value={formData.specifications.brightness}
                  onChange={handleInputChange}
                  placeholder="8000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="specifications.powerConsumption">소비전력</label>
                <input
                  type="text"
                  id="specifications.powerConsumption"
                  name="specifications.powerConsumption"
                  value={formData.specifications.powerConsumption}
                  onChange={handleInputChange}
                  placeholder="500W"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="specifications.installationDate">설치일</label>
                <input
                  type="date"
                  id="specifications.installationDate"
                  name="specifications.installationDate"
                  value={formData.specifications.installationDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* 네트워크 설정 */}
          <div className="form-section">
            <h3>🌐 네트워크 설정</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="network.ipAddress">IP 주소</label>
                <input
                  type="text"
                  id="network.ipAddress"
                  name="network.ipAddress"
                  value={formData.network.ipAddress}
                  onChange={handleInputChange}
                  placeholder="192.168.1.100"
                  className={errors.ipAddress ? 'error' : ''}
                />
                {errors.ipAddress && <span className="error-message">{errors.ipAddress}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="network.port">포트</label>
                <input
                  type="number"
                  id="network.port"
                  name="network.port"
                  value={formData.network.port}
                  onChange={handleInputChange}
                  placeholder="8080"
                  min="1"
                  max="65535"
                  className={errors.port ? 'error' : ''}
                />
                {errors.port && <span className="error-message">{errors.port}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="network.connectionType">연결 타입</label>
                <select
                  id="network.connectionType"
                  name="network.connectionType"
                  value={formData.network.connectionType}
                  onChange={handleInputChange}
                >
                  <option value="tcp">TCP</option>
                  <option value="udp">UDP</option>
                  <option value="mqtt">MQTT</option>
                </select>
              </div>
            </div>
          </div>

          {/* 오류 메시지 */}
          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          {/* 버튼 */}
          <div className="form-actions">
            <button
              type="button"
              className="test-device-button"
              onClick={generateTestDevice}
              disabled={loading}
            >
              🧪 테스트 장비 생성
            </button>

            <div className="action-buttons">
              <button
                type="button"
                className="cancel-button"
                onClick={onClose}
                disabled={loading}
              >
                취소
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? '추가 중...' : '💾 장비 추가'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceAddModal; 